/**
 * Custom Kalman Filter Implementation for FlowRide GPS Filtering
 * A simple, robust Kalman filter specifically designed for GPS position filtering
 * Replaces the external kalman-filter library to avoid dimension and configuration issues
 */

/**
 * Configuration for the Kalman filter
 */
export interface KalmanConfig {

    /** Process noise variance (how much we expect the state to change) */
    "processNoise": number;

    /** Measurement noise variance (how much we trust the measurements) */
    "measurementNoise": number;

    /** Initial state estimate */
    "initialState": number;

    /** Initial error covariance */
    "initialCovariance": number;
}

/**
 * State of the Kalman filter
 */
interface KalmanState {

    /** Current state estimate */
    "state": number;

    /** Current error covariance */
    "covariance": number;
}

/**
 * Custom Kalman Filter for 1D state estimation
 * Optimized for GPS position filtering (latitude/longitude)
 */
export class CustomKalmanFilter {

    private config!: KalmanConfig;

    private state!: KalmanState;

    private initialized: boolean = false;

    /**
     * Create a new Kalman filter
     * @param config - Filter configuration
     */
    constructor( config: KalmanConfig ) {
        this.config = config;
        this.state = {
            "state":      config.initialState,
            "covariance": config.initialCovariance,
        };
    }

    /**
     * Predict the next state (time update)
     * @param deltaTime - Time elapsed since last update (in seconds)
     * @returns Predicted state
     */
    public predict( deltaTime: number = 1.0 ): number {
        /*
         * For constant position model, state doesn't change
         * Only covariance increases due to process noise
         */
        this.state.covariance += this.config.processNoise * deltaTime;

        return this.state.state;
    }

    /**
     * Update the state with a new measurement (measurement update)
     * @param measurement - New measurement value
     * @returns Updated state estimate
     */
    public update( measurement: number ): number {
        if ( !this.initialized ) {
            // First measurement: initialize the filter
            this.state.state = measurement;
            this.state.covariance = this.config.initialCovariance;
            this.initialized = true;

            return this.state.state;
        }

        // Calculate Kalman gain
        const kalmanGain =
            this.state.covariance / ( this.state.covariance + this.config.measurementNoise );

        // Update state estimate
        this.state.state = this.state.state + kalmanGain * ( measurement - this.state.state );

        // Update error covariance
        this.state.covariance = ( 1 - kalmanGain ) * this.state.covariance;

        return this.state.state;
    }

    /**
     * Get the current state estimate
     * @returns Current state
     */
    public getState(): number {
        return this.state.state;
    }

    /**
     * Get the current error covariance
     * @returns Current covariance
     */
    public getCovariance(): number {
        return this.state.covariance;
    }

    /**
     * Reset the filter to initial state
     */
    public reset(): void {
        this.state = {
            "state":      this.config.initialState,
            "covariance": this.config.initialCovariance,
        };
        this.initialized = false;
    }

    /**
     * Update configuration
     * @param newConfig - New configuration
     */
    public updateConfig( newConfig: Partial<KalmanConfig> ): void {
        this.config = {
            ...this.config,
            ...newConfig,
        };
    }

    /**
     * Check if the filter is initialized
     * @returns True if initialized
     */
    public isInitialized(): boolean {
        return this.initialized;
    }

}

/**
 * Predefined configurations for different GPS filtering scenarios
 */
export const KalmanConfigs = {
    /**
     * Configuration for GPS position filtering (latitude/longitude)
     * High measurement noise due to GPS inaccuracy
     */
    "GPS_POSITION": {
        "processNoise":      0.1, // Low process noise (position changes slowly)
        "measurementNoise":  10.0, // High measurement noise (GPS is noisy)
        "initialState":      0,
        "initialCovariance": 1000.0, // High initial uncertainty
    } as KalmanConfig,

    /**
     * Configuration for GPS heading filtering
     * Moderate noise levels for compass/GPS heading
     */
    "GPS_HEADING": {
        "processNoise":      1.0, // Moderate process noise (heading can change)
        "measurementNoise":  5.0, // Moderate measurement noise
        "initialState":      0,
        "initialCovariance": 100.0,
    } as KalmanConfig,

    /**
     * Configuration for GPS speed filtering
     * Low process noise (speed changes gradually)
     */
    "GPS_SPEED": {
        "processNoise":      0.5, // Low process noise (speed changes gradually)
        "measurementNoise":  2.0, // Moderate measurement noise
        "initialState":      0,
        "initialCovariance": 50.0,
    } as KalmanConfig,
};

/**
 * Utility class for creating Kalman filters with common configurations
 */
export class KalmanFilterFactory {

    /**
     * Create a GPS position filter
     * @param initialValue - Initial position value
     * @returns Configured Kalman filter
     */
    public static createPositionFilter( initialValue: number = 0 ): CustomKalmanFilter {
        const config = {
            ...KalmanConfigs.GPS_POSITION,
            "initialState": initialValue,
        };

        return new CustomKalmanFilter( config );
    }

    /**
     * Create a GPS heading filter
     * @param initialValue - Initial heading value
     * @returns Configured Kalman filter
     */
    public static createHeadingFilter( initialValue: number = 0 ): CustomKalmanFilter {
        const config = {
            ...KalmanConfigs.GPS_HEADING,
            "initialState": initialValue,
        };

        return new CustomKalmanFilter( config );
    }

    /**
     * Create a GPS speed filter
     * @param initialValue - Initial speed value
     * @returns Configured Kalman filter
     */
    public static createSpeedFilter( initialValue: number = 0 ): CustomKalmanFilter {
        const config = {
            ...KalmanConfigs.GPS_SPEED,
            "initialState": initialValue,
        };

        return new CustomKalmanFilter( config );
    }

}


